import { html, TemplateResult, PropertyValues } from 'lit';
import { WEB_COMPONENT_EASM_SCHEDULE_CONTROLLER } from '../../../../webcomponents.constants';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { store } from '../../../../redux/store';
import { selectDashboardFilterValues } from '../../../../genericDashboard/redux/dashboard.filters.slice';
import { GenericDashboardsFiltersHelper } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { translateFn } from '../../../../utils/translator.helper';
import moment from 'moment-timezone';
import { getTimezone } from '../../../../utils/global.utils';
import { ButtonColors, ButtonHeights, ButtonTypes } from '../../../../ui/gzButton/types';
import { EASMScanState, EasmScheduleControllerPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { openScanConfigurationModal } from '../../../utils';
import { ConsoleRequest, DataProviderType } from '../../../../utils/request';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { setRefreshWidgets } from '../../../../genericDashboard/redux/dashboards.widgets.slice';
import { getErrorMessage } from '../../../utils';
import { query } from 'lit/decorators.js';
import { EASMIconSet } from '../../../types/iconSet';

@customElement(WEB_COMPONENT_EASM_SCHEDULE_CONTROLLER)
export class GzEasmScheduleControllerWebComponent extends GzGenericWidgetContent {
  @property({
    type: WidgetStateFrontEnd,
    attribute: true,
    reflect: true,
  })
  state: WidgetStateFrontEnd;

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  payload: EasmScheduleControllerPayload;

  @query('#scan-now')
  scanNowButton: HTMLElement;

  @query('#scan-configuration')
  scanConfigurationButton: HTMLElement;

  @query('#tooltip-scan-now')
  tooltipScanNow: HTMLElement;

  @query('#tooltip-scan-configuration')
  tooltipScanConfiguration: HTMLElement;

  public static get styles() {
    return styles;
  }

  updated(properties: PropertyValues): void {
    if (
      (properties.has('currentWidthInterval') && properties.get('currentWidthInterval')) ||
      (properties.has('state') && properties.get('state'))
    ) {
      setTimeout(() => this.updateButtons(), 0);
    }
  }

  protected handleResize(): void {
    super.handleResize();

    this.updateButtons();
  }

  protected updateButtons() {
    if (this.scanNowButton && this.scanConfigurationButton) {
      this.scanNowButton.style.display = 'flex';
      this.scanConfigurationButton.style.display = 'flex';
      const scanNowButtonRect = this.scanNowButton.getBoundingClientRect();
      const scanConfigurationButtonRect = this.scanConfigurationButton.getBoundingClientRect();

      const isOverlapping = !(
        scanNowButtonRect.right < scanConfigurationButtonRect.left ||
        scanNowButtonRect.left > scanConfigurationButtonRect.right ||
        scanNowButtonRect.bottom < scanConfigurationButtonRect.top ||
        scanNowButtonRect.top > scanConfigurationButtonRect.bottom
      );
      if (isOverlapping) {
        this.tooltipScanNow.style.display = 'flex';
        this.tooltipScanConfiguration.style.display = 'flex';
        this.scanNowButton.style.display = 'none';
        this.scanConfigurationButton.style.display = 'none';
      } else {
        this.tooltipScanNow.style.display = 'none';
        this.tooltipScanConfiguration.style.display = 'none';
        this.scanNowButton.style.display = 'flex';
        this.scanConfigurationButton.style.display = 'flex';
      }
    }
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader rows="1" columns="1" style="margin-top: calc(var(--spacer) * 8)"></gz-loader>`;
  }

  private renderLastScan(): TemplateResult {
    if (this.payload?.easmSchedule?.scanState === EASMScanState.RUNNING) {
      return html`<gz-title class="date-area section-value d-flex justify-center" fontWeight="400" fontSize="16">${translateFn('Scan in progress')}</gz-title>`;
    }

    return html`<gz-title class="date-area section-value d-flex justify-center" fontWeight="400" fontSize="16"
      >${this.payload?.easmSchedule?.lastScanDate
        ? moment(this.payload?.easmSchedule.lastScanDate).tz(getTimezone()).format('DD MMMM YYYY, HH:mm')
        : '--'}</gz-title
    >`;
  }

  private renderErrorBtn(): TemplateResult {
    if (this.payload?.easmSchedule?.scanState === EASMScanState.ERROR || this.state === WidgetStateFrontEnd.IS_ERROR) {
      return html`
        <gz-tooltip tooltipText="${this.payload?.easmSchedule?.error ?? translateFn('The scan has failed. Please try again later. If the issue persists, contact Technical Support.')}" id="schedule-error-tooltip">
          <gz-icon-button
            icon="ds-icon-notification_negative"
            size="30"
            fontsize="22"
            buttoncolor="${ButtonColors.NEGATIVE}"
            buttontype="square"
          ></gz-icon-button>
        </gz-tooltip>`;
    }

    return html``;
  }

  private renderScanNowBtn(): TemplateResult {
    if (this.payload?.easmSchedule?.scanState === EASMScanState.RUNNING) {
      return html` <gz-button type="${ButtonTypes.FULL}" color="${ButtonColors.PRIMARY}" showspinner></gz-button>`;
    }

    const isScanNowButtonDisabled = this.state !== WidgetStateFrontEnd.IS_ACTIVE;

    const button = html` <gz-button
      id="scan-now"
      buttonHeight="${ButtonHeights.SMALL}"
      type="${ButtonTypes.OUTLINE}"
      color="${ButtonColors.PRIMARY}"
      @click=${this.scanNow}
      ?disabled=${isScanNowButtonDisabled}
    >
      ${translateFn('Scan now')}
    </gz-button>`;

    if (this.state === WidgetStateFrontEnd.IS_EMPTY) {
      return html` <gz-tooltip tooltipText="${translateFn('No assets included in scan')}"> ${button} </gz-tooltip>`;
    }

    return button;
  }

  protected renderContent(): TemplateResult {
    const scanConfigurationToolTip = html` <gz-tooltip
      @click=${this.navigateToEditSchedule}
      id="tooltip-scan-configuration"
      tooltipText="${translateFn('Scan configuration')}"
      style="display:none"
    >
      ${EASMIconSet.createIconSet('icon-scan-configuration')}
    </gz-tooltip>`;
    const scanNowToolTip = html` <gz-tooltip
      @click=${this.scanNow}
      ?disabled=${this.state !== WidgetStateFrontEnd.IS_ACTIVE}
      id="tooltip-scan-now"
      tooltipText="${translateFn('Scan now')}"
      style="display:none"
    >
      ${EASMIconSet.createIconSet('icon-scan-now')}
    </gz-tooltip>`;

    return html`
      <div id="easmDashboardContainer">
        <div id="last-scan-container" class="widget-section error-section">
          <gz-title class="section-title d-flex justify-center" fontSize="16">${translateFn('Last scan')}</gz-title>
          ${this.renderErrorBtn()}
          ${this.renderLastScan()}
          <div class="widget-scan-button">${scanNowToolTip} ${this.renderScanNowBtn()}</div>
        </div>
        <div id="next-schedule-container" class="widget-section">
          <gz-title class="section-title d-flex justify-center" fontSize="16">${translateFn('Next scheduled scan')}</gz-title>
          <gz-title class="date-area section-value d-flex justify-center" fontSize="16"
            >${this.payload?.easmSchedule?.nextScanDate
              ? moment(this.payload?.easmSchedule.nextScanDate).tz(getTimezone()).format('DD MMMM YYYY, HH:mm')
              : '--'}</gz-title
          >
          <span class="btn-wrapper">
            ${scanConfigurationToolTip}
            <gz-button
              id="scan-configuration"
              buttonHeight="${ButtonHeights.SMALL}"
              type="${ButtonTypes.OUTLINE}"
              color="${ButtonColors.SECONDARY}"
              @click=${this.navigateToEditSchedule}
            >
              ${translateFn('Scan configuration')}
            </gz-button>
          </span>
        </div>
      </div>
    `;
  }

  public injectDependencies(dependencies: any): void {
    this.dependencies = dependencies;
  }

  protected navigateToEditSchedule(): void {
    const companyId = this.getFilterCompanyId();

    if (companyId) {
      openScanConfigurationModal(companyId, this.dependencies);
    }
  }

  protected async scanNow(): Promise<void> {
    try {
      const result = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'EASMScanConfigurations',
        method: 'runScanForCompany',
        payload: {
          companyId: this.getFilterCompanyId(),
        },
      });

      if (result.success) {
        GzToastNotificationWebComponent.buildToastWrapperElement({
          message: translateFn('Scan has been started'),
          severity: ToastSeverity.SUCCESS,
        });
        store.dispatch(setRefreshWidgets(true));
      } else {
        GzToastNotificationWebComponent.buildToastWrapperElement({
          message: getErrorMessage(result.reason, translateFn('Failed to start scan')),
          severity: ToastSeverity.ERROR,
        });
      }
    } catch (error: any) {
      if (error?.err.exceptionType === 'Exceptions\\CannotFindIntegrationSettings') {
        GzToastNotificationWebComponent.buildToastWrapperElement({
          message: this.dependencies.translateFn(
            'EASM integration is not configured. Please contact your administrator.'
          ),
          severity: ToastSeverity.ERROR,
        });
      }
    }
  }

  private getFilterCompanyId(): string | undefined {
    const dashboardAppState = store.getState().dashboardsApp;
    const filters = selectDashboardFilterValues(dashboardAppState);
    return GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters);
  }
}
